import { useState, useEffect } from 'react';
import { authFetch } from '../utils/authFetch';

interface SubscriptionInfo {
  planCode: string;
  planName: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isTrial: boolean;
}

interface CreditBalance {
  available: number;
  reserved: number;
  total: number;
}

interface TrialInfo {
  trialEndsAt: string | null;
  trialCreditsRemaining: number;
  trialDaysRemaining: number | null;
}

/**
 * Billing page - shows subscription status, credits, and upgrade options
 */
export function BillingPage() {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [trial, setTrial] = useState<TrialInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Distinct from "no data": an authentication failure must say so rather than
  // being flattened into an empty-state message.
  const [authFailed, setAuthFailed] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isManaging, setIsManaging] = useState(false);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    // These endpoints are behind header-only `jwtAuth`. This page used to call
    // them with `credentials: 'include'` and no Authorization header, so both
    // always returned 401 — and the page rendered that as "No subscription
    // found" and "Unable to load credit balance". A workspace with a live paid
    // subscription was told it had none. Same helper and same token source as
    // the settings BillingTab, which was already correct.
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setAuthFailed(true);
      setIsLoading(false);
      return;
    }

    try {
      const [subRes, creditsRes] = await Promise.all([
        authFetch('/api/subscription/me', token),
        authFetch('/api/credits/balance', token),
      ]);

      // An auth failure is not an absence of data, and must never be rendered
      // as one: "you have no subscription" is a statement about the account,
      // and making it on the strength of a 401 is simply false.
      if (subRes.status === 401 || subRes.status === 403) {
        setAuthFailed(true);
        setIsLoading(false);
        return;
      }

      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);

        // Extract trial info if in trial
        if (subData.isTrial || subData.status === 'trialing') {
          setTrial({
            trialEndsAt: subData.trialEndsAt || subData.currentPeriodEnd,
            trialCreditsRemaining: subData.trialCreditsRemaining || 0,
            trialDaysRemaining: subData.trialDaysRemaining || null,
          });
        }
      }

      if (creditsRes.status === 401 || creditsRes.status === 403) {
        setAuthFailed(true);
      } else if (creditsRes.ok) {
        const creditsData = await creditsRes.json();
        // A zero balance is a real answer. Rendering it as "unable to load"
        // would report a working API as broken.
        setCredits(creditsData);
      }
    } catch (err) {
      setError('Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const response = await authFetch('/api/billing/stripe/checkout', localStorage.getItem('admin_token') ?? '', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to start checkout');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsManaging(true);
    try {
      const response = await authFetch('/api/billing/stripe/portal', localStorage.getItem('admin_token') ?? '', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to open billing portal');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsManaging(false);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string, isTrial: boolean) => {
    if (isTrial) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
          Trial
        </span>
      );
    }

    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      trialing: 'bg-blue-100 text-blue-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Billing</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your subscription and credits</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Trial Banner */}
      {trial && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-blue-900">
                {trial.trialDaysRemaining !== null && trial.trialDaysRemaining > 0
                  ? `${trial.trialDaysRemaining} days left in your trial`
                  : 'Your trial is ending soon'}
              </h3>
              <p className="text-sm text-blue-700 mt-1">
                Trial credits remaining: {trial.trialCreditsRemaining}
              </p>
            </div>
            <button
              onClick={handleUpgrade}
              disabled={isUpgrading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {isUpgrading ? 'Loading...' : 'Upgrade Now'}
            </button>
          </div>
        </div>
      )}

      {/* Subscription Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Current Plan</h2>

        {subscription ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xl font-semibold text-gray-900 capitalize">
                  {subscription.planName || subscription.planCode}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {subscription.currentPeriodEnd && !subscription.isTrial && (
                    <>Next billing: {formatDate(subscription.currentPeriodEnd)}</>
                  )}
                  {subscription.isTrial && trial?.trialEndsAt && (
                    <>Trial ends: {formatDate(trial.trialEndsAt)}</>
                  )}
                </p>
              </div>
              {getStatusBadge(subscription.status, subscription.isTrial)}
            </div>

            {subscription.cancelAtPeriodEnd && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  Your subscription will be canceled at the end of the current period.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              {subscription.status === 'active' && (
                <button
                  onClick={handleManageSubscription}
                  disabled={isManaging}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  {isManaging ? 'Loading...' : 'Manage Subscription'}
                </button>
              )}
              {(subscription.status === 'trialing' || subscription.isTrial) && (
                <button
                  onClick={handleUpgrade}
                  disabled={isUpgrading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {isUpgrading ? 'Loading...' : 'Upgrade to Paid'}
                </button>
              )}
            </div>
          </div>
        ) : authFailed ? (
          <p className="text-gray-500">
            Your session has expired. Sign in again to see your subscription.
          </p>
        ) : (
          <p className="text-gray-500">No subscription found</p>
        )}
      </div>

      {/* Credits Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Credit Balance</h2>

        {credits ? (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-semibold text-gray-900">{credits.available}</p>
              <p className="text-sm text-gray-500">Available</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-semibold text-gray-900">{credits.reserved}</p>
              <p className="text-sm text-gray-500">Reserved</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-semibold text-gray-900">{credits.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
        ) : authFailed ? (
          <p className="text-gray-500">
            Your session has expired. Sign in again to see your credit balance.
          </p>
        ) : (
          <p className="text-gray-500">Unable to load credit balance</p>
        )}
      </div>
    </div>
  );
}
